import React, { useRef, useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';
import SuccessModal from './SuccessModal';
import { RECAPTCHA_SITE_KEY, EMAILJS_CONFIG } from '../config';
import { ChevronLeft, ChevronRight, Check, User, FileText, Heart, Settings, Target, Phone, Users } from 'lucide-react';

// Style constants
const inputStyle = "w-full bg-[#EEF7F9] border border-gray-300 rounded-xl px-5 py-4 text-lg text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#008080] transition-colors";

// Steps configuration with icons
const STEPS = [
    { id: 0, title: 'Basic Info', icon: User, optional: true },
    { id: 1, title: 'Participant', icon: FileText, optional: false },
    { id: 2, title: 'Guardian', icon: Users, optional: true },
    { id: 3, title: 'NDIS Info', icon: Heart, optional: false },
    { id: 4, title: 'Support', icon: Settings, optional: true },
    { id: 5, title: 'Goals', icon: Target, optional: true },
    { id: 6, title: 'Submit', icon: Phone, optional: false }
];

// Step Icons Navigation Component
const StepIcons = ({ currentStep, steps }) => (
    <div className="flex items-center justify-center mb-8">
        {steps.map((step, index) => {
            const Icon = step.icon;
            const isCompleted = index < currentStep;
            const isCurrent = index === currentStep;

            return (
                <React.Fragment key={index}>
                    <div className="flex flex-col items-center">
                        <div
                            className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all duration-300 ${isCompleted
                                ? 'bg-[#008080] text-white'
                                : isCurrent
                                    ? 'bg-[#D99C3B] text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-400 border-2 border-gray-200'
                                }`}
                        >
                            {isCompleted ? <Check size={24} /> : <Icon size={22} />}
                        </div>
                        <span className={`text-xs mt-2 font-medium hidden md:block ${isCurrent ? 'text-[#D99C3B]' : isCompleted ? 'text-[#008080]' : 'text-gray-400'
                            }`}>
                            {step.title}
                        </span>
                    </div>
                    {index < steps.length - 1 && (
                        <div className={`w-8 md:w-16 h-0.5 mx-1 md:mx-2 ${index < currentStep ? 'bg-[#008080]' : 'bg-gray-200'
                            }`} />
                    )}
                </React.Fragment>
            );
        })}
    </div>
);

// Progress Bar Component
const ProgressBar = ({ currentStep, totalSteps }) => {
    const progress = ((currentStep + 1) / totalSteps) * 100;
    return (
        <div className="w-full bg-gray-200 rounded-full h-2 mb-8">
            <div
                className="bg-[#008080] h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
            />
        </div>
    );
};

const RegisterForm = () => {
    const form = useRef();
    const recaptchaRef = useRef();
    const [status, setStatus] = useState('');
    const [captchaToken, setCaptchaToken] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);

    // Form data state to preserve values across steps
    const [formData, setFormData] = useState({
        // Referrer
        referrer_name: '',
        referrer_email: '',
        referrer_phone: '',
        referrer_dob: '',
        referrer_agency: '',
        // Participant
        participant_name: '',
        participant_gender: '',
        participant_country: '',
        participant_dob: '',
        participant_indigenous: '',
        participant_languages: '',
        participant_email: '',
        participant_phone: '',
        participant_address: '',
        participant_city: '',
        participant_state: '',
        participant_postcode: '',
        // Guardian
        guardian_name: '',
        guardian_address: '',
        guardian_relationship: '',
        // NDIS
        ndis_number: '',
        plan_start: '',
        plan_end: '',
        // Support & Funding
        support_ratio: '',
        support_needs: '',
        plan_management: '',
        sil_funding: '',
        plan_manager_name: '',
        invoice_details: '',
        contact_details: '',
        sta: '',
        service_description: '',
        // Diagnosis
        diagnosis_primary: '',
        diagnosis_secondary: '',
        diagnosis_other: '',
        restrictive_practices: '',
        restrictive_details: '',
        // Goals
        goal_1: '',
        goal_2: '',
        goal_3: '',
        goal_4: '',
        goal_5: '',
    });

    // Track if referrer has content
    const hasReferrerContent = formData.referrer_name.trim() !== '';

    // Track validation errors
    const [stepErrors, setStepErrors] = useState({});

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field when user types
        if (stepErrors[name]) {
            setStepErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Validation for each step
    const validateStep = (stepIndex) => {
        const errors = {};

        // Step 0: Basic contact info is required + Referrer validation if name provided
        if (stepIndex === 0) {
            // Required participant fields at Step 0
            if (!formData.participant_name.trim()) {
                errors.participant_name = 'Name is required';
            }
            // Email is optional - only validate format if provided
            if (formData.participant_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.participant_email)) {
                errors.participant_email = 'Please enter a valid email address';
            }
            if (!formData.participant_phone.trim()) {
                errors.participant_phone = 'Phone is required';
            } else if (!/^[0-9]{10}$/.test(formData.participant_phone)) {
                errors.participant_phone = 'Phone must be 10 digits';
            }

            // Referrer validation - if name is provided, other fields become required
            if (hasReferrerContent) {
                if (!formData.referrer_agency.trim()) {
                    errors.referrer_agency = 'Referrer Agency is required when name is provided';
                }
                if (!formData.referrer_email.trim()) {
                    errors.referrer_email = 'Referrer Email is required when name is provided';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.referrer_email)) {
                    errors.referrer_email = 'Please enter a valid email address';
                }
                if (!formData.referrer_phone.trim()) {
                    errors.referrer_phone = 'Referrer Phone is required when name is provided';
                } else if (!/^[0-9]{10}$/.test(formData.referrer_phone)) {
                    errors.referrer_phone = 'Phone must be 10 digits';
                }
                // Referrer DOB is optional - only validate format/year if provided
                if (formData.referrer_dob.trim()) {
                    const dobMatch = formData.referrer_dob.match(/^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(\d{4})$/);
                    if (!dobMatch) {
                        errors.referrer_dob = 'Format must be dd-mm-yyyy';
                    } else {
                        const year = parseInt(dobMatch[3], 10);
                        const currentYear = new Date().getFullYear();
                        if (year <= 1900 || year > currentYear) {
                            errors.referrer_dob = `Year must be between 1901 and ${currentYear}`;
                        }
                    }
                }
            }
        }

        // Step 1: Participant Details - all required fields
        if (stepIndex === 1) {
            if (!formData.participant_name.trim()) {
                errors.participant_name = 'Full Name is required';
            }
            if (!formData.participant_gender) {
                errors.participant_gender = 'Gender is required';
            }
            if (!formData.participant_dob.trim()) {
                errors.participant_dob = 'Date of Birth is required';
            } else {
                const dobMatch = formData.participant_dob.match(/^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(\d{4})$/);
                if (!dobMatch) {
                    errors.participant_dob = 'Format must be dd-mm-yyyy';
                } else {
                    const year = parseInt(dobMatch[3], 10);
                    const currentYear = new Date().getFullYear();
                    if (year <= 1900 || year > currentYear) {
                        errors.participant_dob = `Year must be between 1901 and ${currentYear}`;
                    }
                }
            }
            if (!formData.participant_indigenous) {
                errors.participant_indigenous = 'Indigenous Status is required';
            }
            if (!formData.participant_email.trim()) {
                errors.participant_email = 'Email is required';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.participant_email)) {
                errors.participant_email = 'Please enter a valid email address';
            }
            if (!formData.participant_phone.trim()) {
                errors.participant_phone = 'Phone is required';
            } else if (!/^[0-9]{10}$/.test(formData.participant_phone)) {
                errors.participant_phone = 'Phone must be 10 digits';
            }
            if (!formData.participant_address.trim()) {
                errors.participant_address = 'Street Address is required';
            }
            if (!formData.participant_city.trim()) {
                errors.participant_city = 'City is required';
            }
            if (!formData.participant_state) {
                errors.participant_state = 'State is required';
            }
            if (!formData.participant_postcode.trim()) {
                errors.participant_postcode = 'Postcode is required';
            }
        }

        // Step 2: Guardian Details - optional, no required fields

        // Step 3: NDIS Information - NDIS number is required, dates optional but must be valid format
        if (stepIndex === 3) {
            if (!formData.ndis_number.trim()) {
                errors.ndis_number = 'NDIS Number is required';
            }
            // Plan Start Date - optional but validate format if provided
            if (formData.plan_start && formData.plan_start.trim()) {
                const startMatch = formData.plan_start.match(/^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(\d{4})$/);
                if (!startMatch) {
                    errors.plan_start = 'Format must be dd-mm-yyyy';
                } else {
                    const year = parseInt(startMatch[3], 10);
                    if (year < 1900 || year > 2100) {
                        errors.plan_start = 'Year must be between 1900 and 2100';
                    }
                }
            }
            // Plan End Date - optional but validate format if provided
            if (formData.plan_end && formData.plan_end.trim()) {
                const endMatch = formData.plan_end.match(/^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(\d{4})$/);
                if (!endMatch) {
                    errors.plan_end = 'Format must be dd-mm-yyyy';
                } else {
                    const year = parseInt(endMatch[3], 10);
                    if (year < 1900 || year > 2100) {
                        errors.plan_end = 'Year must be between 1900 and 2100';
                    }
                }
            }
        }

        // Step 4: Support & Services - optional, no required fields
        // Step 5: Goals - optional, no required fields

        setStepErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Navigation
    const nextStep = () => {
        if (validateStep(currentStep)) {
            if (currentStep < STEPS.length - 1) {
                setCurrentStep(currentStep + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Date validation
    const validateDateFormat = (fieldName) => {
        const dateStr = formData[fieldName];
        if (!dateStr) return true;

        const input = form.current.elements[fieldName];
        const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
        const match = dateStr.match(regex);

        if (!match) {
            input.setCustomValidity("Format must be dd-mm-yyyy (e.g., 15-08-1990).");
            input.reportValidity();
            return false;
        }

        const dd = parseInt(match[1], 10);
        const mm = parseInt(match[2], 10);
        const yyyy = parseInt(match[3], 10);
        const currentYear = new Date().getFullYear();

        if (yyyy <= 1900 || yyyy > currentYear) {
            input.setCustomValidity(`Year must be between 1901 and ${currentYear}.`);
            input.reportValidity();
            return false;
        }

        if (dd < 1 || dd > 31) {
            input.setCustomValidity("Day must be between 01 and 31.");
            input.reportValidity();
            return false;
        }

        if (mm < 1 || mm > 12) {
            input.setCustomValidity("Month must be between 01 and 12.");
            input.reportValidity();
            return false;
        }

        input.setCustomValidity("");
        return true;
    };

    // Submit handler
    const sendEmail = (e) => {
        e.preventDefault();

        if (!validateDateFormat('referrer_dob')) return;
        if (!validateDateFormat('participant_dob')) return;
        if (!validateDateFormat('plan_start')) return;
        if (!validateDateFormat('plan_end')) return;

        if (!captchaToken) {
            alert('Please complete the reCAPTCHA verification.');
            return;
        }

        setStatus('sending');

        emailjs.sendForm(
            EMAILJS_CONFIG.SERVICE_ID,
            EMAILJS_CONFIG.TEMPLATES.REGISTER,
            form.current,
            EMAILJS_CONFIG.PUBLIC_KEY
        )
            .then((result) => {
                console.log(result.text);
                setStatus('success');
                form.current.reset();
                setCaptchaToken(null);
                recaptchaRef.current?.reset();
                setCurrentStep(0);
                setFormData({
                    referrer_name: '', referrer_email: '', referrer_phone: '', referrer_dob: '', referrer_agency: '',
                    participant_name: '', participant_gender: '', participant_country: '', participant_dob: '',
                    participant_indigenous: '', participant_languages: '', participant_email: '', participant_phone: '',
                    participant_address: '', participant_city: '', participant_state: '', participant_postcode: '',
                    guardian_name: '', guardian_address: '', guardian_relationship: '',
                    ndis_number: '', plan_start: '', plan_end: '',
                    support_ratio: '', support_needs: '', plan_management: '', sil_funding: '',
                    plan_manager_name: '', invoice_details: '', contact_details: '', sta: '', service_description: '',
                    diagnosis_primary: '', diagnosis_secondary: '', diagnosis_other: '',
                    restrictive_practices: '', restrictive_details: '',
                    goal_1: '', goal_2: '', goal_3: '', goal_4: '', goal_5: '',
                });
            }, (error) => {
                console.log(error.text);
                setStatus('error');
            });
    };

    // Step visibility
    const isVisible = (stepIndex) => currentStep === stepIndex;

    // Current step info
    const currentStepInfo = STEPS[currentStep];

    return (
        <section className="py-16 bg-white scroll-mt-32" id="register">
            {status === 'success' && (
                <SuccessModal
                    onClose={() => setStatus('')}
                    title="Thank You!"
                    message="Registration Received"
                    subMessage="We have received your details and will be in touch with you shortly."
                />
            )}

            <div className="container mx-auto px-4 max-w-4xl">
                {/* Header */}
                <div className="text-center mb-8">
                    <h2 className="text-[#D99C3B] font-serif text-4xl md:text-5xl mb-3">Registration</h2>
                    <p className="text-gray-500 text-lg">Join Community Plus Care Services</p>
                </div>

                {/* Step Icons */}
                <StepIcons currentStep={currentStep} steps={STEPS} />

                {/* Progress Bar */}
                <ProgressBar currentStep={currentStep} totalSteps={STEPS.length} />

                <form
                    ref={form}
                    onSubmit={sendEmail}
                    onKeyDown={(e) => {
                        // Prevent Enter key from submitting form before final step
                        if (e.key === 'Enter' && currentStep < STEPS.length - 1) {
                            e.preventDefault();
                        }
                    }}
                >
                    {/* All form fields stay in DOM for email submission */}

                    {/* STEP 0: Basic Info / Referrer (Optional) */}
                    <div className={isVisible(0) ? 'block' : 'hidden'}>
                        <div className="mb-6">
                            <h3 className="text-[#D99C3B] font-serif text-2xl mb-2">Let's Start With Your Contact</h3>
                            <p className="text-gray-500">Tell us how to reach you. * = Required</p>
                        </div>

                        {/* Basic contact fields - linked to participant details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <input
                                    type="text"
                                    name="participant_name"
                                    placeholder="Your Name *"
                                    value={formData.participant_name}
                                    onChange={handleChange}
                                    className={`${inputStyle} ${stepErrors.participant_name ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                />
                                {stepErrors.participant_name && <p className="text-red-500 text-sm mt-1">{stepErrors.participant_name}</p>}
                            </div>
                            <div>
                                <input
                                    type="email"
                                    name="participant_email"
                                    placeholder="Email Address"
                                    value={formData.participant_email}
                                    onChange={handleChange}
                                    className={`${inputStyle} ${stepErrors.participant_email ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                />
                                {stepErrors.participant_email && <p className="text-red-500 text-sm mt-1">{stepErrors.participant_email}</p>}
                            </div>
                            <div>
                                <input
                                    type="tel"
                                    name="participant_phone"
                                    placeholder="Phone Number *"
                                    pattern="[0-9]{10}"
                                    maxLength={10}
                                    value={formData.participant_phone}
                                    onChange={handleChange}
                                    className={`${inputStyle} ${stepErrors.participant_phone ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                />
                                {stepErrors.participant_phone && <p className="text-red-500 text-sm mt-1">{stepErrors.participant_phone}</p>}
                            </div>
                            <input
                                type="text"
                                name="referrer_name"
                                placeholder="Referrer Name (if applicable)"
                                value={formData.referrer_name}
                                onChange={handleChange}
                                className={inputStyle}
                            />
                        </div>

                        {/* Hidden fields when referrer name not provided */}
                        {!hasReferrerContent && (
                            <>
                                <input type="hidden" name="referrer_agency" value="" />
                                <input type="hidden" name="referrer_email" value="" />
                                <input type="hidden" name="referrer_phone" value="" />
                                <input type="hidden" name="referrer_dob" value="" />
                            </>
                        )}

                        {/* Show Referrer Details section if referrer name is entered */}
                        {hasReferrerContent && (
                            <div className="mt-6">
                                <h4 className="text-[#D99C3B] font-serif text-xl mb-4">Referrer Details</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <input
                                            type="text"
                                            name="referrer_display_name"
                                            placeholder="Name"
                                            value={formData.referrer_name}
                                            readOnly
                                            className={`${inputStyle} bg-gray-100`}
                                        />
                                    </div>
                                    <div>
                                        <input
                                            type="email"
                                            name="referrer_email"
                                            placeholder="Email"
                                            value={formData.referrer_email}
                                            onChange={handleChange}
                                            className={`${inputStyle} ${stepErrors.referrer_email ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                        />
                                        {stepErrors.referrer_email && <p className="text-red-500 text-sm mt-1">{stepErrors.referrer_email}</p>}
                                    </div>
                                    <div>
                                        <input
                                            type="tel"
                                            name="referrer_phone"
                                            placeholder="Phone"
                                            pattern="[0-9]{10}"
                                            maxLength={10}
                                            value={formData.referrer_phone}
                                            onChange={handleChange}
                                            className={`${inputStyle} ${stepErrors.referrer_phone ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                        />
                                        {stepErrors.referrer_phone && <p className="text-red-500 text-sm mt-1">{stepErrors.referrer_phone}</p>}
                                    </div>
                                    <div>
                                        <input
                                            type="text"
                                            name="referrer_dob"
                                            placeholder="Date of Birth (dd-mm-yyyy)"
                                            pattern="(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}"
                                            maxLength={10}
                                            value={formData.referrer_dob}
                                            onChange={handleChange}
                                            className={`${inputStyle} ${stepErrors.referrer_dob ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                        />
                                        {stepErrors.referrer_dob && <p className="text-red-500 text-sm mt-1">{stepErrors.referrer_dob}</p>}
                                    </div>
                                    <div className="md:col-span-2">
                                        <input
                                            type="text"
                                            name="referrer_agency"
                                            placeholder="Referrer Agency"
                                            value={formData.referrer_agency}
                                            onChange={handleChange}
                                            className={`${inputStyle} ${stepErrors.referrer_agency ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                        />
                                        {stepErrors.referrer_agency && <p className="text-red-500 text-sm mt-1">{stepErrors.referrer_agency}</p>}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* STEP 1: Participant Details (Required) */}
                    <div className={isVisible(1) ? 'block' : 'hidden'}>
                        <div className="mb-6">
                            <h3 className="text-[#D99C3B] font-serif text-2xl mb-2">Participant Details</h3>
                            <p className="text-gray-500">Information about the person receiving support. * = Required</p>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <input
                                        type="text"
                                        placeholder="Full Name *"
                                        value={formData.participant_name}
                                        readOnly
                                        className={`${inputStyle} bg-gray-100 cursor-not-allowed`}
                                        title="Name entered in Step 1"
                                    />
                                    <p className="text-gray-400 text-xs mt-1">From Basic Info</p>
                                </div>
                                <div>
                                    <select
                                        name="participant_gender"
                                        value={formData.participant_gender}
                                        onChange={handleChange}
                                        className={`${inputStyle} appearance-none cursor-pointer ${stepErrors.participant_gender ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                    >
                                        <option value="" disabled>Gender *</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Non-binary">Non-binary</option>
                                        <option value="Prefer not to say">Prefer not to say</option>
                                    </select>
                                    {stepErrors.participant_gender && <p className="text-red-500 text-sm mt-1">{stepErrors.participant_gender}</p>}
                                </div>
                                <input
                                    type="text"
                                    name="participant_country"
                                    placeholder="Country of Birth"
                                    value={formData.participant_country}
                                    onChange={handleChange}
                                    className={inputStyle}
                                />
                                <div>
                                    <input
                                        type="text"
                                        name="participant_dob"
                                        placeholder="Date of Birth (dd-mm-yyyy) *"
                                        pattern="(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}"
                                        maxLength={10}
                                        value={formData.participant_dob}
                                        onChange={handleChange}
                                        className={`${inputStyle} ${stepErrors.participant_dob ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                    />
                                    {stepErrors.participant_dob && <p className="text-red-500 text-sm mt-1">{stepErrors.participant_dob}</p>}
                                </div>
                                <div>
                                    <select
                                        name="participant_indigenous"
                                        value={formData.participant_indigenous}
                                        onChange={handleChange}
                                        className={`${inputStyle} appearance-none cursor-pointer ${stepErrors.participant_indigenous ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                    >
                                        <option value="" disabled>Indigenous Status *</option>
                                        <option value="Aboriginal">Aboriginal</option>
                                        <option value="Torres Strait Islander">Torres Strait Islander</option>
                                        <option value="Both">Both</option>
                                        <option value="Neither">Neither</option>
                                    </select>
                                    {stepErrors.participant_indigenous && <p className="text-red-500 text-sm mt-1">{stepErrors.participant_indigenous}</p>}
                                </div>
                                <input
                                    type="text"
                                    name="participant_languages"
                                    placeholder="Languages Spoken"
                                    value={formData.participant_languages}
                                    onChange={handleChange}
                                    className={inputStyle}
                                />
                            </div>

                            <h4 className="text-gray-700 font-semibold mt-6 pt-4 border-t">Contact & Address</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <input
                                        type="email"
                                        placeholder="Email *"
                                        value={formData.participant_email}
                                        readOnly
                                        className={`${inputStyle} bg-gray-100 cursor-not-allowed`}
                                        title="Email entered in Step 1"
                                    />
                                    <p className="text-gray-400 text-xs mt-1">From Basic Info</p>
                                </div>
                                <div>
                                    <input
                                        type="tel"
                                        placeholder="Phone *"
                                        value={formData.participant_phone}
                                        readOnly
                                        className={`${inputStyle} bg-gray-100 cursor-not-allowed`}
                                        title="Phone entered in Step 1"
                                    />
                                    <p className="text-gray-400 text-xs mt-1">From Basic Info</p>
                                </div>
                                <div className="md:col-span-2">
                                    <input
                                        type="text"
                                        name="participant_address"
                                        placeholder="Street Address *"
                                        value={formData.participant_address}
                                        onChange={handleChange}
                                        className={`${inputStyle} ${stepErrors.participant_address ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                    />
                                    {stepErrors.participant_address && <p className="text-red-500 text-sm mt-1">{stepErrors.participant_address}</p>}
                                </div>
                                <div>
                                    <input
                                        type="text"
                                        name="participant_city"
                                        placeholder="City *"
                                        value={formData.participant_city}
                                        onChange={handleChange}
                                        className={`${inputStyle} ${stepErrors.participant_city ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                    />
                                    {stepErrors.participant_city && <p className="text-red-500 text-sm mt-1">{stepErrors.participant_city}</p>}
                                </div>
                                <div>
                                    <select
                                        name="participant_state"
                                        value={formData.participant_state}
                                        onChange={handleChange}
                                        className={`${inputStyle} appearance-none cursor-pointer ${stepErrors.participant_state ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                    >
                                        <option value="" disabled>State *</option>
                                        <option value="SA">SA</option>
                                        <option value="QLD">QLD</option>
                                        <option value="NSW">NSW</option>
                                        <option value="VIC">VIC</option>
                                        <option value="TAS">TAS</option>
                                        <option value="WA">WA</option>
                                        <option value="ACT">ACT</option>
                                        <option value="NT">NT</option>
                                    </select>
                                    {stepErrors.participant_state && <p className="text-red-500 text-sm mt-1">{stepErrors.participant_state}</p>}
                                </div>
                                <div>
                                    <input
                                        type="text"
                                        name="participant_postcode"
                                        placeholder="Postcode *"
                                        value={formData.participant_postcode}
                                        onChange={handleChange}
                                        className={`${inputStyle} ${stepErrors.participant_postcode ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                    />
                                    {stepErrors.participant_postcode && <p className="text-red-500 text-sm mt-1">{stepErrors.participant_postcode}</p>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* STEP 2: Guardian Details (Optional) */}
                    <div className={isVisible(2) ? 'block' : 'hidden'}>
                        <div className="mb-6">
                            <h3 className="text-[#D99C3B] font-serif text-2xl mb-2">Guardian Details</h3>
                            <p className="text-gray-500">Add guardian or nominee details if applicable. All fields are optional.</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                type="text"
                                name="guardian_name"
                                placeholder="Guardian Name"
                                value={formData.guardian_name}
                                onChange={handleChange}
                                className={inputStyle}
                            />
                            <input
                                type="text"
                                name="guardian_address"
                                placeholder="Guardian Address"
                                value={formData.guardian_address}
                                onChange={handleChange}
                                className={inputStyle}
                            />
                            <div className="md:col-span-2">
                                <select
                                    name="guardian_relationship"
                                    value={formData.guardian_relationship}
                                    onChange={handleChange}
                                    className={`${inputStyle} appearance-none cursor-pointer`}
                                >
                                    <option value="" disabled>Relationship to Participant</option>
                                    <option value="Parent">Parent</option>
                                    <option value="Sibling">Sibling</option>
                                    <option value="Legal Guardian">Legal Guardian</option>
                                    <option value="Nominee">Nominee</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* STEP 3: NDIS Information (Required) */}
                    <div className={isVisible(3) ? 'block' : 'hidden'}>
                        <div className="mb-6">
                            <h3 className="text-[#D99C3B] font-serif text-2xl mb-2">NDIS Information</h3>
                            <p className="text-gray-500">Your NDIS plan details. * = Required</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <input
                                    type="text"
                                    name="ndis_number"
                                    placeholder="NDIS Number *"
                                    value={formData.ndis_number}
                                    onChange={handleChange}
                                    className={`${inputStyle} ${stepErrors.ndis_number ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                />
                                {stepErrors.ndis_number && <p className="text-red-500 text-sm mt-1">{stepErrors.ndis_number}</p>}
                            </div>
                            <div>
                                <input
                                    type="text"
                                    name="plan_start"
                                    placeholder="Plan Start Date (dd-mm-yyyy)"
                                    pattern="(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}"
                                    maxLength={10}
                                    value={formData.plan_start}
                                    onChange={handleChange}
                                    className={`${inputStyle} ${stepErrors.plan_start ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                />
                                {stepErrors.plan_start && <p className="text-red-500 text-sm mt-1">{stepErrors.plan_start}</p>}
                            </div>
                            <div>
                                <input
                                    type="text"
                                    name="plan_end"
                                    placeholder="Plan End Date (dd-mm-yyyy)"
                                    pattern="(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}"
                                    maxLength={10}
                                    value={formData.plan_end}
                                    onChange={handleChange}
                                    className={`${inputStyle} ${stepErrors.plan_end ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                />
                                {stepErrors.plan_end && <p className="text-red-500 text-sm mt-1">{stepErrors.plan_end}</p>}
                            </div>
                        </div>
                    </div>

                    {/* STEP 4: Funding, Services & Support (Optional) */}
                    <div className={isVisible(4) ? 'block' : 'hidden'}>
                        <div className="mb-6">
                            <h3 className="text-[#D99C3B] font-serif text-2xl mb-2">Support & Services</h3>
                            <p className="text-gray-500">Tell us about your support needs. All fields are optional.</p>
                        </div>

                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <select
                                    name="support_ratio"
                                    value={formData.support_ratio}
                                    onChange={handleChange}
                                    className={`${inputStyle} appearance-none cursor-pointer`}
                                >
                                    <option value="" disabled>Required Support Ratio</option>
                                    <option value="1:1">1:1</option>
                                    <option value="1:2">1:2</option>
                                    <option value="1:3">1:3</option>
                                    <option value="2:1">2:1</option>
                                    <option value="Other">Other</option>
                                </select>
                                <select
                                    name="support_needs"
                                    value={formData.support_needs}
                                    onChange={handleChange}
                                    className={`${inputStyle} appearance-none cursor-pointer`}
                                >
                                    <option value="" disabled>Support Needs</option>
                                    <option value="Standard">Standard</option>
                                    <option value="High Intensity">High Intensity</option>
                                    <option value="Complex">Complex</option>
                                </select>
                                <select
                                    name="plan_management"
                                    value={formData.plan_management}
                                    onChange={handleChange}
                                    className={`${inputStyle} appearance-none cursor-pointer`}
                                >
                                    <option value="" disabled>Plan Management</option>
                                    <option value="Self Managed">Self Managed</option>
                                    <option value="Plan Managed">Plan Managed</option>
                                    <option value="NDIA Managed">NDIA Managed</option>
                                </select>
                                <select
                                    name="sil_funding"
                                    value={formData.sil_funding}
                                    onChange={handleChange}
                                    className={`${inputStyle} appearance-none cursor-pointer`}
                                >
                                    <option value="" disabled>SIL Funding</option>
                                    <option value="Approved">Approved</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Not Applicable">Not Applicable</option>
                                </select>
                                <input
                                    type="text"
                                    name="plan_manager_name"
                                    placeholder="Plan Manager Name"
                                    value={formData.plan_manager_name}
                                    onChange={handleChange}
                                    className={inputStyle}
                                />
                                <input
                                    type="text"
                                    name="invoice_details"
                                    placeholder="Invoice Details"
                                    value={formData.invoice_details}
                                    onChange={handleChange}
                                    className={inputStyle}
                                />
                                <div className="md:col-span-2">
                                    <input
                                        type="text"
                                        name="contact_details"
                                        placeholder="Plan Manager Contact Details"
                                        value={formData.contact_details}
                                        onChange={handleChange}
                                        className={inputStyle}
                                    />
                                </div>
                            </div>

                            <h4 className="text-gray-700 font-semibold pt-4 border-t">Services Required</h4>
                            <div className="space-y-3">
                                {['Supported Independent Living (SIL)', 'Community and Social Participation', 'Respite Care'].map(service => (
                                    <label key={service} className="flex items-center space-x-4 cursor-pointer p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                        <input type="checkbox" name="services" value={service} className="w-6 h-6 text-[#008080] rounded focus:ring-[#008080]" />
                                        <span className="text-gray-700 text-lg">{service}</span>
                                    </label>
                                ))}
                            </div>
                            <input
                                type="text"
                                name="sta"
                                placeholder="STA (Short Term Accommodation)"
                                value={formData.sta}
                                onChange={handleChange}
                                className={inputStyle}
                            />
                            <textarea
                                name="service_description"
                                placeholder="Describe your service needs (hours, days, etc.)"
                                rows="3"
                                value={formData.service_description}
                                onChange={handleChange}
                                className={inputStyle}
                            ></textarea>

                            <h4 className="text-gray-700 font-semibold pt-4 border-t">Diagnosis & Restrictive Practices</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    name="diagnosis_primary"
                                    placeholder="Primary Diagnosis"
                                    value={formData.diagnosis_primary}
                                    onChange={handleChange}
                                    className={inputStyle}
                                />
                                <input
                                    type="text"
                                    name="diagnosis_secondary"
                                    placeholder="Secondary Diagnosis"
                                    value={formData.diagnosis_secondary}
                                    onChange={handleChange}
                                    className={inputStyle}
                                />
                                <input
                                    type="text"
                                    name="diagnosis_other"
                                    placeholder="Other Diagnoses"
                                    value={formData.diagnosis_other}
                                    onChange={handleChange}
                                    className={inputStyle}
                                />
                                <select
                                    name="restrictive_practices"
                                    value={formData.restrictive_practices}
                                    onChange={handleChange}
                                    className={`${inputStyle} appearance-none cursor-pointer`}
                                >
                                    <option value="" disabled>Restrictive Practices?</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                                <div className="md:col-span-2">
                                    <input
                                        type="text"
                                        name="restrictive_details"
                                        placeholder="If yes, provide details"
                                        value={formData.restrictive_details}
                                        onChange={handleChange}
                                        className={inputStyle}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* STEP 5: Goals (Optional) */}
                    <div className={isVisible(5) ? 'block' : 'hidden'}>
                        <div className="mb-6">
                            <h3 className="text-[#D99C3B] font-serif text-2xl mb-2">Your Goals</h3>
                            <p className="text-gray-500">What goals would you like to achieve? All fields are optional.</p>
                        </div>

                        <div className="space-y-4">
                            <input
                                type="text"
                                name="goal_1"
                                placeholder="Goal 1 - e.g., Live more independently"
                                value={formData.goal_1}
                                onChange={handleChange}
                                className={inputStyle}
                            />
                            <input
                                type="text"
                                name="goal_2"
                                placeholder="Goal 2 - e.g., Join community activities"
                                value={formData.goal_2}
                                onChange={handleChange}
                                className={inputStyle}
                            />
                            <input
                                type="text"
                                name="goal_3"
                                placeholder="Goal 3 - e.g., Learn new skills"
                                value={formData.goal_3}
                                onChange={handleChange}
                                className={inputStyle}
                            />
                            <input
                                type="text"
                                name="goal_4"
                                placeholder="Goal 4"
                                value={formData.goal_4}
                                onChange={handleChange}
                                className={inputStyle}
                            />
                            <input
                                type="text"
                                name="goal_5"
                                placeholder="Goal 5"
                                value={formData.goal_5}
                                onChange={handleChange}
                                className={inputStyle}
                            />
                        </div>
                    </div>

                    {/* STEP 6: Submit */}
                    <div className={isVisible(6) ? 'block' : 'hidden'}>
                        <div className="text-center">
                            <div className="w-20 h-20 bg-[#008080] rounded-full flex items-center justify-center mx-auto mb-6">
                                <Check size={40} className="text-white" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">Ready to Submit!</h3>
                            <p className="text-gray-600 mb-8">
                                Please verify the reCAPTCHA below and submit your registration.
                            </p>

                            <div className="flex justify-center mb-6">
                                <ReCAPTCHA
                                    key="register-recaptcha"
                                    ref={recaptchaRef}
                                    sitekey={RECAPTCHA_SITE_KEY}
                                    onChange={(token) => setCaptchaToken(token)}
                                    onExpired={() => setCaptchaToken(null)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-100">
                        {/* Back Button */}
                        <button
                            type="button"
                            onClick={prevStep}
                            disabled={currentStep === 0}
                            className={`flex items-center gap-2 px-6 py-4 rounded-full font-semibold text-lg transition-all duration-300 ${currentStep === 0
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-[#008080] hover:bg-gray-100'
                                }`}
                        >
                            <ChevronLeft size={24} />
                            Back
                        </button>

                        {/* Continue/Submit Button */}
                        {currentStep < STEPS.length - 1 ? (
                            <button
                                type="button"
                                onClick={nextStep}
                                className="flex items-center gap-2 bg-[#008080] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                            >
                                Continue
                                <ChevronRight size={24} />
                            </button>
                        ) : (
                            <button
                                type="submit"
                                disabled={status === 'sending'}
                                className="flex items-center gap-2 bg-[#D99C3B] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-amber-600 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
                            >
                                {status === 'sending' ? 'Submitting...' : 'Submit Registration'}
                                <Check size={24} />
                            </button>
                        )}
                    </div>

                    {status === 'error' && (
                        <div className="mt-6 text-center text-red-600 font-medium">
                            Submission failed. Please try again.
                        </div>
                    )}
                </form>

                {/* Call Alternative */}
                <div className="mt-12 text-center p-6 bg-gray-50 rounded-2xl">
                    <p className="text-gray-600 mb-2">Prefer to talk to someone?</p>
                    <p className="text-[#008080] font-bold text-xl">Call us: 0489 235 263</p>
                </div>
            </div>
        </section>
    );
};

export default RegisterForm;
